/**
 * Logger battery and signal for the phone layout's bad-state chips
 * ("Battery 10%", "Weak signal"). Thresholds: low battery at 20% or less,
 * weak signal at 2 bars or fewer (the legacy 0-9 scale).
 */

/** At or below this battery percentage the logger needs attention. */
export const LOW_BATTERY_PCT = 20;
/** At or below this many signal bars the signal is weak. */
export const WEAK_SIGNAL_BARS = 2;

/**
 * A location's battery as a percentage, or null when unknown.
 *
 * Prefers `battery_pct` whenever the reply carries it (null there means the
 * logger sent no code). Older replies only send `battery` text ('80%'); the
 * backend maps a missing code to '0%' there, so a parsed 0 is unknown, not
 * flat.
 */
export const batteryPercent = (loc: {
  battery_pct?: number | null;
  battery?: string | null;
}): number | null => {
  if (loc.battery_pct !== undefined) {
    return typeof loc.battery_pct === 'number' && Number.isFinite(loc.battery_pct)
      ? loc.battery_pct
      : null;
  }
  const match = /^\s*(\d+(?:\.\d+)?)\s*%?\s*$/.exec(loc.battery ?? '');
  if (!match) return null;
  const pct = Number(match[1]);
  return Number.isFinite(pct) && pct > 0 ? pct : null;
};

export const isBatteryLow = (pct: number | null): boolean =>
  pct !== null && pct <= LOW_BATTERY_PCT;

/**
 * Signal bars from the logger's raw signal text: the leading mode letter of
 * 'M3' / 'Z2' is dropped, giving 3 / 2. 'X' (no signal reported), an empty
 * value or anything else gives null.
 */
export const signalBars = (signal?: string | null): number | null => {
  const match = /^\s*[A-Za-z]?\s*(\d+)\s*$/.exec(signal ?? '');
  return match ? Number(match[1]) : null;
};

export const isSignalWeak = (bars: number | null): boolean =>
  bars !== null && bars <= WEAK_SIGNAL_BARS;
